import React, { useMemo } from "react";
import { cn } from "@/lib/utils";
import {
  strategyRdBodyTextClassName,
  strategyRdCaptionClassName,
  strategyRdSectionCardClassName,
  strategyRdTooltipArrowClassName,
  strategyRdTooltipClassName,
} from "./strategyRdLabUi";
import { Area, AreaChart, CartesianGrid, ReferenceDot, ReferenceLine, XAxis, YAxis } from "recharts";
import { Info } from "lucide-react";

import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

import { ChartContainer, ChartTooltip, type ChartConfig } from "@/components/ui/chart";
import type { StrategyRdEquityCurvePoint } from "../../../types/strategyRd";
import { formatStrategyRdMoney, formatStrategyRdPct } from "../../../utils/strategyRd";
import { PNL_NEGATIVE_COLOR, PNL_POSITIVE_COLOR } from "../Statistics/components/statsSignedPnlBarUtils";
import ChartCard from "../Dashboard/components/ChartCard";

/** Above the starting balance: profit green, like every other P&L on the dashboard. */
const SURF = PNL_POSITIVE_COLOR;
const config: ChartConfig = {
  equity: { label: "Equity", color: SURF },
  drawdown: { label: "Below the high", color: PNL_NEGATIVE_COLOR },
};

type Row = { i: number; label: string; equity: number; drawdownPct: number; ms: number | null };

function toMs(raw: string | null | undefined): number | null {
  if (!raw) return null;
  const t = new Date(raw).getTime();
  return Number.isFinite(t) ? t : null;
}

function spanWords(ms: number): string {
  const days = ms / 86_400_000;
  if (days >= 60) return `${Math.round(days / 30)} months`;
  if (days >= 1.5) return `${Math.round(days)} days`;
  const hours = ms / 3_600_000;
  return hours >= 1 ? `${Math.round(hours)} hours` : "under an hour";
}

/** Drawdown facts from the equity path: deepest dip, longest time below a high, recovery. */
function drawdownStats(rows: Row[]) {
  let peak = rows[0].equity;
  let peakAt = 0;
  let worst = { pct: 0, money: 0, at: 0, fromPeak: 0 };
  let longest = { from: 0, to: 0 };
  for (let k = 0; k < rows.length; k++) {
    const r = rows[k];
    if (r.equity >= peak) {
      if (k - peakAt > longest.to - longest.from) longest = { from: peakAt, to: k };
      peak = r.equity;
      peakAt = k;
    } else if (r.drawdownPct < worst.pct) {
      worst = { pct: r.drawdownPct, money: peak - r.equity, at: k, fromPeak: peakAt };
    }
  }
  const last = rows.length - 1;
  const openUnderwater = rows[last].equity < peak;
  if (openUnderwater && last - peakAt > longest.to - longest.from) longest = { from: peakAt, to: last };
  // Recovery from the deepest dip: the first point back at that dip's earlier high.
  let recoveredAt: number | null = null;
  const highBefore = rows[worst.fromPeak]?.equity;
  for (let k = worst.at; k < rows.length; k++) {
    if (highBefore != null && rows[k].equity >= highBefore) {
      recoveredAt = k;
      break;
    }
  }
  const span = (a: number, b: number) => {
    const ma = rows[a]?.ms;
    const mb = rows[b]?.ms;
    return ma != null && mb != null && mb > ma ? spanWords(mb - ma) : `${b - a} trades`;
  };
  return {
    worstPct: worst.pct,
    worstMoney: worst.money,
    longest: longest.to > longest.from ? span(longest.from, longest.to) : null,
    recovery: worst.pct < 0 ? (recoveredAt != null ? span(worst.at, recoveredAt) : "not yet") : null,
    currentPct: rows[last].drawdownPct,
  };
}

function TipRow({ label, value, color }: { label: string; value: string; color?: string }): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium tabular-nums" style={color ? { color } : undefined}>
        {value}
      </span>
    </div>
  );
}

function Metric({ label, value, hint, tone }: { label: string; value: string; hint: string; tone?: "neg" }) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <span className="flex items-center gap-1">
        <span className={strategyRdCaptionClassName}>{label}</span>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="inline-flex size-4 items-center justify-center text-muted-foreground/70 transition-colors hover:text-[color:var(--brand-tropical-surf)]"
              aria-label={`What is ${label}?`}
            >
              <Info className="size-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent className={cn(strategyRdTooltipClassName, "max-w-64 text-xs leading-relaxed")} arrowClassName={strategyRdTooltipArrowClassName}>{hint}</TooltipContent>
        </Tooltip>
      </span>
      <span
        className="font-ibm-plex text-lg font-semibold leading-tight tabular-nums text-gray-600 dark:text-white"
        style={tone === "neg" ? { color: PNL_NEGATIVE_COLOR } : undefined}
      >
        {value}
      </span>
    </div>
  );
}

function shortDate(raw: string, withDay = false): string {
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return withDay
    ? d.toLocaleDateString(undefined, { month: "short", day: "numeric" })
    : d.toLocaleDateString(undefined, { month: "short", year: "2-digit" });
}

/**
 * The account over the backtest, first thing under the headline numbers: where money was made
 * and lost. One line: Tropical Surf above the starting balance, red below it, so the winning
 * and losing stretches read at a glance. Drawdown is a number under the chart and in the hover.
 */
export function StrategyRdEquityHero({
  points,
  startingEquity,
  currency,
  headerAction,
  marginCallTimes,
}: {
  points: StrategyRdEquityCurvePoint[] | null | undefined;
  startingEquity?: number | null;
  currency: string | null;
  /** e.g. the Combined / Longs / Shorts toggle. */
  headerAction?: React.ReactNode;
  /** When the broker would have force-closed part of a position (margin call). */
  marginCallTimes?: string[] | null;
}): JSX.Element | null {
  const rows = useMemo((): Row[] => {
    const out: Row[] = [];
    let peak = -Infinity;
    // The curve may open with its own start point (no time, before the first trade) — keep one start.
    const firstPoint = points?.[0];
    const hasOwnStart = firstPoint != null && !firstPoint.date && !firstPoint.t && !firstPoint.bar_time;
    const trades = hasOwnStart ? (points ?? []).slice(1) : points ?? [];
    const start = startingEquity ?? firstPoint?.equity ?? null;
    if (start != null && trades.length) {
      out.push({ i: 0, label: trades[0].date || trades[0].t || "Start", equity: start, drawdownPct: 0, ms: null });
      peak = start;
    }
    trades.forEach((p, k) => {
      const eq = Number(p.equity);
      if (!Number.isFinite(eq)) return;
      peak = Math.max(peak, eq);
      const label = p.date || p.t || p.bar_time || String(k + 1);
      out.push({
        i: k + 1,
        label,
        equity: eq,
        drawdownPct: peak > 0 ? -((peak - eq) / peak) * 100 : 0,
        ms: toMs(p.date || p.t || p.bar_time),
      });
    });
    return out;
  }, [points, startingEquity]);
  if (rows.length < 3) {
    // Too few trades on this side to draw a curve. Keep the card and its Combined / Longs /
    // Shorts switch: returning nothing removed the switch too, so there was no way back.
    const start = startingEquity ?? rows[0]?.equity ?? null;
    const end = rows[rows.length - 1]?.equity ?? start;
    const n = Math.max(0, rows.length - 1);
    return (
      <ChartCard
        title="Account over the backtest"
        percentage={start && end != null ? ((end - start) / start) * 100 : 0}
        value={end ?? undefined}
        valueFormatter={(v) => formatStrategyRdMoney(v, currency) ?? String(v)}
        showViewSelector={false}
        headerAction={headerAction}
        chart={
          <div
            className={cn("flex h-[120px] items-center justify-center px-4 text-center", strategyRdBodyTextClassName)}
            data-strategy-rd-equity-hero-empty=""
          >
            {n === 0
              ? "No trades on this side in this backtest."
              : `Only ${n} trade${n === 1 ? "" : "s"} on this side, not enough to draw the account.`}
          </div>
        }
      />
    );
  }
  const first = rows[0];
  const last = rows[rows.length - 1];
  const worst = rows.reduce((a, b) => (b.drawdownPct < a.drawdownPct ? b : a), rows[0]);
  const up = last.equity >= first.equity;
  const lo = Math.min(...rows.map((r) => r.equity));
  const hi = Math.max(...rows.map((r) => r.equity));
  const pad = hi > lo ? (hi - lo) * 0.08 : Math.max(Math.abs(hi) * 0.01, 1);
  const money = (v: number) => formatStrategyRdMoney(v, currency) ?? String(v);
  const tickEvery = Math.max(1, Math.round(rows.length / 6));
  // Short backtests: "Sep 24"; long ones: "Sep 26" (month + year).
  const spanMs = (rows[rows.length - 1].ms ?? 0) - (rows.find((r) => r.ms != null)?.ms ?? 0);
  const withDay = spanMs > 0 && spanMs < 120 * 86_400_000;

  const returnPct = first.equity > 0 ? ((last.equity - first.equity) / first.equity) * 100 : 0;
  // Where the starting balance sits in the chart's height (0 = top): the line and fill turn red
  // below it, so the stretches spent losing money read at a glance.
  // The dotted line is the starting balance. Each gradient spans its own shape, not the axis: the
  // line runs from the highest to the lowest balance, the fill from the highest balance down to
  // the bottom of the chart — so the switch point is worked out for each.
  const clamp01 = (v: number) => Math.min(1, Math.max(0, v));
  const strokeOffset = hi > lo ? clamp01((hi - first.equity) / (hi - lo)) : first.equity >= hi ? 1 : 0;
  const fillBottom = lo - pad;
  const fillOffset = hi > fillBottom ? clamp01((hi - first.equity) / (hi - fillBottom)) : 0;
  const pct = (v: number) => formatStrategyRdPct(v) ?? "N/A";
  const dd = drawdownStats(rows);
  // Each margin call on the first equity point at or after it.
  const callRows = (marginCallTimes ?? [])
    .map((t) => toMs(t))
    .filter((t): t is number => t != null)
    .map((t) => rows.find((r) => r.ms != null && r.ms >= t))
    .filter((r): r is Row => Boolean(r))
    .filter((r, k, arr) => arr.findIndex((x) => x.i === r.i) === k);

  const chart = (
    <div className="flex flex-col gap-0" data-strategy-rd-equity-hero="">
      <ChartContainer config={config} className="aspect-auto h-[260px] w-full">
        <AreaChart data={rows} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}>
          <defs>
            <linearGradient id="srdEquityFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={SURF} stopOpacity={0.28} />
              <stop offset={fillOffset} stopColor={SURF} stopOpacity={0.03} />
              <stop offset={fillOffset} stopColor={PNL_NEGATIVE_COLOR} stopOpacity={0.03} />
              <stop offset="100%" stopColor={PNL_NEGATIVE_COLOR} stopOpacity={0.2} />
            </linearGradient>
            <linearGradient id="srdEquityStroke" x1="0" y1="0" x2="0" y2="1">
              <stop offset={strokeOffset} stopColor={SURF} />
              <stop offset={strokeOffset} stopColor={PNL_NEGATIVE_COLOR} />
            </linearGradient>
          </defs>
          <CartesianGrid vertical={false} strokeDasharray="3 3" />
          <XAxis
            dataKey="i"
            tickLine={false}
            axisLine={false}
            interval={tickEvery - 1}
            tickFormatter={(i) => shortDate(rows[Number(i)]?.label ?? "", withDay)}
            fontSize={11}
          />
          <YAxis
            width={72}
            tickLine={false}
            axisLine={false}
            domain={[lo - pad, hi + pad]}
            tickCount={4}
            tickFormatter={(v) => money(Number(v))}
          />
          <ChartTooltip
            content={({ active, payload }) => {
              const r = active ? (payload?.[0]?.payload as Row | undefined) : undefined;
              if (!r) return null;
              const fromStart = first.equity > 0 ? ((r.equity - first.equity) / first.equity) * 100 : 0;
              return (
                <div className="grid min-w-[11rem] gap-1 rounded-[6px] border border-border bg-popover px-2.5 py-2 text-xs text-popover-foreground shadow-md">
                  <p className="font-medium">{`${shortDate(r.label, true)} · trade ${r.i}`}</p>
                  <TipRow label="Account" value={money(r.equity)} />
                  <TipRow label="Since the start" value={pct(fromStart)} color={fromStart >= 0 ? PNL_POSITIVE_COLOR : PNL_NEGATIVE_COLOR} />
                  <TipRow
                    label="Below its high"
                    value={r.drawdownPct < 0 ? pct(r.drawdownPct) : "At a high"}
                    color={r.drawdownPct < 0 ? PNL_NEGATIVE_COLOR : undefined}
                  />
                </div>
              );
            }}
          />
          <ReferenceLine
            y={first.equity}
            stroke="var(--muted-foreground)"
            strokeOpacity={0.5}
            strokeDasharray="4 4"
            label={{ value: "Starting balance", position: "insideBottomLeft", fontSize: 10, fill: "var(--muted-foreground)" }}
          />
          <Area type="monotone" dataKey="equity" stroke="url(#srdEquityStroke)" strokeWidth={2} fill="url(#srdEquityFill)" isAnimationActive={false} />
          {callRows.map((r) => (
            <ReferenceDot key={`mc-${r.i}`} x={r.i} y={r.equity} r={5} fill={PNL_NEGATIVE_COLOR} stroke="var(--background)" strokeWidth={2} />
          ))}
          <ReferenceDot x={last.i} y={last.equity} r={4} fill={up ? PNL_POSITIVE_COLOR : PNL_NEGATIVE_COLOR} stroke="white" />
        </AreaChart>
      </ChartContainer>
    </div>
  );

  return (
    // One section: the chart card and its drawdown strip share a border, joined by a hairline.
    <div
      className="flex flex-col gap-4"
      data-strategy-rd-equity-section=""
    >
      <ChartCard
        title="Account over the backtest"
        percentage={returnPct}
        value={last.equity}
        valueFormatter={money}
        showViewSelector={false}
        headerAction={headerAction}
        // Under the ending balance, away from the chart.
        subtitle={
          <>
            From {money(first.equity)} · deepest dip{" "}
            <span style={{ color: PNL_NEGATIVE_COLOR }}>{formatStrategyRdPct(worst.drawdownPct) ?? "N/A"}</span> ·{" "}
            {rows.length - 1} trades
          </>
        }
        chart={chart}
      />
      {/* Under the chart, not inside it: the card's chart area has a fixed height. */}
      <div
        // Its own card, full width under the chart card, with its stroke.
        className={cn(strategyRdSectionCardClassName, "px-[22px] py-5")}
        data-strategy-rd-drawdown-section=""
      >
        <div className="grid grid-cols-2 gap-x-6 gap-y-5 sm:grid-cols-4" data-strategy-rd-drawdown-stats="">
          <Metric
            label="Max drawdown"
            value={`${formatStrategyRdPct(dd.worstPct) ?? "N/A"} · ${money(-dd.worstMoney)}`}
            hint="The biggest fall from a high point of the account to a later low, before a new high. Hover the chart to see how far below its last high the account was at any trade."
            tone="neg"
          />
          <Metric
            label="Longest below a high"
            value={dd.longest ?? "N/A"}
            hint="The longest stretch the account spent under its previous high: how long you would have waited for a new high."
          />
          <Metric
            label="Recovery from max drawdown"
            value={dd.recovery ?? "N/A"}
            hint="How long it took to climb back from the deepest dip to the high before it. 'Not yet' means the backtest ended below that high."
          />
          <Metric
            label="Drawdown now"
            value={formatStrategyRdPct(dd.currentPct) ?? "N/A"}
            hint="How far below its highest point the account was at the end of the backtest."
            tone={dd.currentPct < 0 ? "neg" : undefined}
          />
        </div>
        {callRows.length > 0 ? (
          <p
            className={cn("flex items-center gap-2 border-t border-border pt-3 text-xs", strategyRdBodyTextClassName)}
            data-strategy-rd-margin-call-legend=""
          >
            <span className="inline-block size-2.5 shrink-0 rounded-full" style={{ backgroundColor: PNL_NEGATIVE_COLOR }} />
            {callRows.length} margin call{callRows.length === 1 ? "" : "s"}: the broker would have force-closed part of
            a position because the account ran out of free margin.
          </p>
        ) : null}
      </div>
    </div>
  );
}
